import { styled } from "goober";
import { useRef } from "react";
import { PUBLIC_ROUTE_AUTH_KILL_SWITCH_FLAG } from "../../../shared/featureFlagNames";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useLogout } from "../../hooks/useLogout";
import { navEnterStyles, useNavTransition } from "../../hooks/useNavTransition";
import { usePrincipalModals } from "../../hooks/usePrincipalModals";
import { useQuery } from "../../hooks/useQuery";
import { useScrollFade } from "../../hooks/useScrollFade";
import { useShowFeatureFlagsNav } from "../../hooks/useShowFeatureFlagsNav";
import { makeUrl, routes, useRouteMatch } from "../../lib/urls";
import { ClickButton } from "../ClickButton";
import { HelpTooltip } from "../HelpTooltip";
import { Icon, type IconName } from "../Icon";
import { Link } from "../Link";
import { Text } from "../Text";

type PatternInput = NonNullable<Parameters<typeof useRouteMatch>[0]>;

const Container = styled("div")`
  width: 228px;
  padding: 12px 12px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: auto;
  flex: 1;
  ${navEnterStyles}
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px),
    black calc(100% - var(--fade-bottom, 0px)),
    transparent
  );
  mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--fade-top, 0px),
    black calc(100% - var(--fade-bottom, 0px)),
    transparent
  );
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SectionHeading = styled("h4")`
  padding: 10px 8px;
  padding-right: 4px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const linkStyles = `
  padding: 8px;
  display: flex;
  border-radius: 8px !important;
  flex-direction: row;
  align-items: center;
  text-decoration: none;
  color: var(--text-primary);
  gap: 8px;
  &:hover {
    background-color: var(--alpha4) !important;
  }
`;

const StyledLink = styled(Link)`
  ${linkStyles}
`;

const NavButton = styled(ClickButton)`
  ${linkStyles}
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const Break = styled("hr")`
  border: none;
  margin: 8px;
  height: 0.5px;
  background-color: var(--border-alpha);
`;

type NavItemProps<T extends PatternInput> = {
  scope: T;
  icon: IconName;
  children: React.ReactNode;
  // Highlight follows `scope`, but the link can target a child (e.g. open
  // Networking on its Rules tab while the parent stays highlighted).
  linkTo?: PatternInput;
  info?: React.ReactNode;
};

function NavItem<T extends PatternInput>({
  scope,
  icon,
  children,
  linkTo,
  info,
}: NavItemProps<T>) {
  const [match] = useRouteMatch(scope);
  const href = makeUrl(linkTo ?? scope);

  return (
    <StyledLink
      to={href}
      style={{ backgroundColor: match ? "var(--alpha4)" : "transparent" }}
    >
      <Icon name={icon} size={16} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          minWidth: 0,
        }}
      >
        <Text ellipsis>{children}</Text>
        {info}
      </div>
    </StyledLink>
  );
}

function ProfileNavItem() {
  const currentUserId = useCurrentUserId();
  const { onOpenUser } = usePrincipalModals();
  // Open at click time: the member modal attaches to whatever page is current,
  // so a href captured at mount would reopen it on that stale page.
  return (
    <NavButton onClick={() => onOpenUser(currentUserId)}>
      <Icon name="circle-user-round" size={16} />
      <Text>Profile</Text>
    </NavButton>
  );
}

export function SettingsSidebar() {
  const navEnter = useNavTransition("settings");
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadPersonalChats = useHasAccess("tenant:personal_chats_read");
  const canManageAI = useHasAccess("tenant:ai_providers_manage");
  const canManageSSO = useHasAccess("tenant:sso_manage");
  const isTenantAdmin = useHasAccess("allow_all");
  const routeAuthKillSwitch = useFeatureFlag(
    PUBLIC_ROUTE_AUTH_KILL_SWITCH_FLAG
  );
  const showSecurity =
    isTenantAdmin && (routeAuthKillSwitch || config.routeAuthForceDisabled);
  const canReadAuditLogs = useHasAccess("tenant:audit_logs_read");
  const canManageFeatureFlags = useHasAccess("tenant:feature_flags_manage");
  const canManageNetworks = useHasAccess("tenant:networks_manage");
  const canCreateNetworks = useHasAccess("tenant:network_create");
  const canManageEgressRoutes = useHasAccess("tenant:egress_routes_manage");
  const canManageEgressRules = useHasAccess("tenant:egress_rules_manage");
  const [visibleNetworks] = useQuery(queries.networks());
  const [apiKeys] = useQuery(queries.apiKeys());
  const showNetworks =
    canManageNetworks ||
    canCreateNetworks ||
    canManageEgressRoutes ||
    canManageEgressRules ||
    visibleNetworks.length > 0;
  const showFeatureFlagsNav = useShowFeatureFlagsNav();
  const logout = useLogout();
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  return (
    <Container ref={scrollRef} data-nav-enter={navEnter}>
      <Section>
        <SectionHeading>
          <Text variant="text-small">Personal</Text>
        </SectionHeading>
        <NavItem scope={routes.settings.preferences} icon="settings-2">
          Preferences
        </NavItem>
        <ProfileNavItem />
      </Section>

      <Break />

      <Section>
        <SectionHeading>
          <Text variant="text-small">Admin</Text>
        </SectionHeading>
        {(canReadSpaces || canReadPersonalChats) && (
          <NavItem
            scope={routes.watchtower}
            icon="tower-control"
            info={
              <HelpTooltip content="Browse all spaces and every member’s personal workflows and chats" />
            }
          >
            Watchtower
          </NavItem>
        )}
        <NavItem scope={routes.settings.spaces} icon="hash">
          Spaces
        </NavItem>
        <NavItem scope={routes.settings.members} icon="id-card">
          Members
        </NavItem>
        <NavItem scope={routes.settings.userGroups} icon="users-round">
          Groups
        </NavItem>
        <NavItem scope={routes.settings.roles} icon="id-card-lanyard">
          Roles
        </NavItem>
        <NavItem scope={routes.settings.mcp} icon="mcp">
          MCP
        </NavItem>
        {canManageAI && (
          <NavItem scope={routes.settings.ai} icon="brain">
            AI providers
          </NavItem>
        )}
        {canManageSSO && (
          <NavItem scope={routes.settings.sso} icon="shield-check">
            SSO
          </NavItem>
        )}
        {showSecurity && (
          <NavItem scope={routes.settings.security} icon="lock">
            Security
          </NavItem>
        )}
        {apiKeys.length > 0 && (
          <NavItem scope={routes.settings.apiKeys} icon="key">
            API keys
          </NavItem>
        )}
        {canReadAuditLogs && (
          <NavItem scope={routes.settings.auditLogs} icon="table">
            Audit logs
          </NavItem>
        )}
        {canManageFeatureFlags && showFeatureFlagsNav && (
          <NavItem scope={routes.settings.featureFlags} icon="ticket">
            Feature flags
          </NavItem>
        )}
        {showNetworks && (
          <NavItem scope={routes.settings.networking} icon="earth">
            Networking
          </NavItem>
        )}
      </Section>

      <div style={{ flex: 1 }} />

      <NavButton onClick={logout}>
        <Icon name="output" size={16} />
        <Text>Log out</Text>
      </NavButton>
    </Container>
  );
}
